import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { MODULES } from "../../config/modules";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { FileText, FolderKanban, Settings, CheckCircle } from "lucide-react";

const iconMap = {
  contracts: FileText,
  proposals: FolderKanban,
  admin: Settings,
};

export default function HomeDashboard() {
  const { profile, checkRole } = useAuth();

  const accessibleModules = MODULES.filter(
    (m) => !m.requiredRole || checkRole(m.requiredRole)
  );

  const getRoleBadgeVariant = (role) => {
    if (role === "super-admin") return "purple";
    if (role === "admin") return "default";
    return "secondary";
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Welcome back</h1>
        <p className="text-muted-foreground mt-1">
          Signed in as <span className="font-medium">{profile?.email}</span>
          <Badge variant={getRoleBadgeVariant(profile?.role)} className="ml-2">
            {profile?.role}
          </Badge>
        </p>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">Modules</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {accessibleModules.map((m) => {
            const Icon = iconMap[m.id] || FileText;
            return (
              <Link key={m.id} to={m.path}>
                <Card className="hover:bg-accent/50 transition-colors cursor-pointer h-full">
                  <CardHeader className="flex flex-row items-center gap-4 space-y-0 pb-2">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{m.label}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {m.id === "contracts" && "View and manage contract opportunities"}
                      {m.id === "proposals" && "Track and manage proposals"}
                      {m.id === "admin" && "System administration and user management"}
                    </CardDescription>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">System Status</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium">Authentication</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium">Firestore</p>
                  <p className="text-sm text-muted-foreground">Connected</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium">User Roles</p>
                  <p className="text-sm text-muted-foreground">Configured</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
